import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { VdiApprovalService, VdiApproval, AzureVmStatus } from '../../services/vdi-approval.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Subject, takeUntil, timer } from 'rxjs';
import { RdpConnection } from '../rdp-client/rdp-client.component';

@Component({
  selector: 'app-vdi-approval-status',
  templateUrl: './vdi-approval-status.component.html',
  styleUrls: ['./vdi-approval-status.component.scss']
})
export class VdiApprovalStatusComponent implements OnInit, OnDestroy {
  @Input() ticketId?: number;
  @Input() ticketNumber?: string;
  @Input() autoRefresh: boolean = true;
  @Output() vmCreationCompleted = new EventEmitter<{ ticketId: number; vmName: string; success: boolean }>();

  approval: VdiApproval | null = null;
  loading = false;
  canApprove = false;
  hasCheckedApproval = false;
  vmDetails: any[] = []; // Cached VM details to avoid infinite change detection
  approvalStatusInfo: any; // Cached to avoid infinite change detection
  vmCreationStatusInfo: any; // Cached to avoid infinite change detection
  azureVmStatus: AzureVmStatus | null = null; // Real-time Azure VM status
  azurePowerStateInfo: any = null; // Cached Azure power state display info
  private destroy$ = new Subject<void>();
  private refreshInterval?: any;
  private azureStatusRefreshInterval?: any;

  constructor(
    private vdiApprovalService: VdiApprovalService,
    private message: NzMessageService,
    private modal: NzModalService
  ) {
    // Initialize cached data to prevent undefined errors
    this.approvalStatusInfo = this.vdiApprovalService.getApprovalStatusInfo(null);
    this.vmCreationStatusInfo = this.vdiApprovalService.getVmCreationStatusInfo(null);
  }

  ngOnInit(): void {
    console.log('🎬 [VDI Approval] Component initialized', {
      ticketId: this.ticketId,
      ticketNumber: this.ticketNumber,
      autoRefresh: this.autoRefresh
    });

    // Subscribe to VDI status update events for immediate UI refresh
    this.subscribeToVdiStatusUpdates();

    if (this.ticketId || this.ticketNumber) {
      this.loadApprovalStatus();
      this.checkApprovalPermissions();

      // Only start auto-refresh if explicitly enabled
      if (this.autoRefresh) {
        console.log('✅ [VDI Approval] Auto-refresh is enabled, starting...');
        this.startAutoRefresh();
      } else {
        console.log('⚠️ [VDI Approval] Auto-refresh is disabled');
      }
      
      // Start Azure VM status monitoring if VM exists
      this.startAzureStatusMonitoring();
    }
  }

  /**
   * Subscribe to VDI status update events for immediate UI refresh
   */
  subscribeToVdiStatusUpdates(): void {
    this.vdiApprovalService.vdiStatusUpdate
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (vdiApproval) => {
          console.log('🚀 [VDI Approval] Received VDI status update event:', vdiApproval);

          // Check if this update is for our current ticket
          if (vdiApproval &&
              (vdiApproval.ticketId === this.ticketId ||
               vdiApproval.ticketNumber === this.ticketNumber)) {
            console.log('🎯 [VDI Approval] Update matches current ticket, refreshing UI...');

            // Update local approval data immediately
            this.approval = vdiApproval;
            this.hasCheckedApproval = true;
            this.loading = false;
            
            // Update all cached data to avoid infinite change detection
            this.updateCachedData();

            // Show success notification for VM creation
            if (vdiApproval.vmCreationStatus === 'Succeeded' && vdiApproval.vmName) {
              this.message.success(`🎉 VM Creation Completed! VM "${vdiApproval.vmName}" is now ready for use.`);

              // Log all VM details for verification
              console.log('📋 [VDI Approval] VM Creation Details:');
              console.log(`   🏷️ VM Name: ${vdiApproval.vmName}`);
              console.log(`   💾 VM Size: ${vdiApproval.vmSize || 'Not specified'}`);
              console.log(`   🌐 Public IP: ${vdiApproval.publicIpAddress || 'Not assigned'}`);
              console.log(`   🔒 Private IP: ${vdiApproval.privateIpAddress || 'Not assigned'}`);
              console.log(`   👨‍💼 Admin Username: ${vdiApproval.adminUsername || 'Not assigned'}`);
              console.log(`   ✅ Completed At: ${vdiApproval.vmCreationCompletedAt || 'Unknown'}`);
            }

            // Stop auto-refresh if VM creation is complete
            if (vdiApproval.vmCreationStatus === 'Succeeded' || vdiApproval.vmCreationStatus === 'Failed') {
              console.log('🛑 [VDI Approval] VM creation final state reached via event, stopping auto-refresh');
              this.stopAutoRefresh();

              // Emit event to notify parent component to refresh ticket list
              console.log('📢 [VDI Approval] Emitting VM creation completed event to refresh ticket list');
              this.vmCreationCompleted.emit({
                ticketId: vdiApproval.ticketId,
                vmName: vdiApproval.vmName || 'Unknown',
                success: vdiApproval.vmCreationStatus === 'Succeeded'
              });
            }
          } else {
            console.log('⚠️ [VDI Approval] Update not for current ticket, ignoring');
          }
        },
        error: (error) => {
          console.error('❌ [VDI Approval] Error in VDI status update subscription:', error);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopAutoRefresh();
    this.stopAzureStatusMonitoring();
  }

  loadApprovalStatus(): void {
    if (!this.ticketId && !this.ticketNumber) {
      console.warn('⚠️ [VDI Approval] No ticket ID or ticket number provided');
      return;
    }

    console.log(`🔄 [VDI Approval] Loading approval status for ticket: ${this.ticketId || this.ticketNumber}`);
    this.loading = true;
    this.hasCheckedApproval = false;
    
    const source = this.ticketId
      ? this.vdiApprovalService.getApprovalStatus(this.ticketNumber!)
      : this.vdiApprovalService.getApprovalStatusByTicketNumber(this.ticketNumber!);

    source.pipe(takeUntil(this.destroy$)).subscribe({
      next: (approval) => {
        console.log('📥 [VDI Approval] Approval data received:', approval);
        this.approval = approval;
        this.hasCheckedApproval = true;
        this.loading = false;
        
        // Update all cached data to avoid infinite change detection
        this.updateCachedData();

        // Only stop auto-refresh if VM creation is complete (Succeeded/Failed), not just approved
        if (approval &&
            (approval.vmCreationStatus === 'Succeeded' || approval.vmCreationStatus === 'Failed')) {
          console.log('🛑 [VDI Approval] VM creation final state detected, stopping auto-refresh');
          this.stopAutoRefresh();
        } else if (approval && approval.approvalStatus === 'Approved') {
          console.log('✅ [VDI Approval] Approval detected, continuing VM creation monitoring');
          console.log('🖥️ [VDI Approval] Current VM Status:', approval.vmCreationStatus || 'NotStarted');
          console.log('⏳ [VDI Approval] Continuing to monitor VM creation progress...');
        }
      },
      error: (error) => {
        console.error('❌ [VDI Approval] Error in loadApprovalStatus:', error);
        this.hasCheckedApproval = true;
        this.loading = false;
        
        // Don't show error message for 404, as it just means no approval record exists
        if (error.status === 404) {
          console.log('ℹ️ [VDI Approval] No approval record found (404) - this is normal for new tickets');
          return;
        }
        
        // Handle timeout errors
        if (error.status === 408 || error.error === 'TimeoutError') {
          this.message.error('Request timed out. Please check if the backend server is running.');
          console.error('⏱️ [VDI Approval] Timeout error - backend may be slow or unreachable');
          return;
        }
        
        // Handle authentication errors
        if (error.status === 403) {
          this.message.error('Authentication required. Please log in again.');
          console.error('🔒 [VDI Approval] Authentication error - user not authorized');
          return;
        }
        
        // Handle network errors
        if (error.status === 0) {
          this.message.error('Cannot connect to server. Please check your network connection.');
          console.error('🌐 [VDI Approval] Network error - backend server unreachable');
          return;
        }
        
        // Generic error message for other errors
        this.message.error(`Failed to load approval status: ${error.message || 'Unknown error'}`);
        console.error('💥 [VDI Approval] Unexpected error:', error);
      },
      complete: () => {
        console.log('✅ [VDI Approval] Request completed');
      }
    });
  }

  checkApprovalPermissions(): void {
    this.vdiApprovalService.canUserApproveTickets()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (canApprove) => {
          this.canApprove = canApprove;
        },
        error: () => {
          this.canApprove = false;
        }
      });
  }

  approveTicket(): void {
    this.showApprovalModal('approve');
  }

  rejectTicket(): void {
    this.showApprovalModal('reject');
  }

  private showApprovalModal(action: 'approve' | 'reject'): void {
    const isApprove = action === 'approve';

    this.modal.create({
      nzTitle: `${isApprove ? 'Approve' : 'Reject'} VDI Request`,
      nzContent: `
        <div>
          <p>Are you sure you want to ${isApprove ? 'approve' : 'reject'} this VDI request?</p>
          ${this.approval ? `
            <div class="approval-details">
              <p><strong>Ticket:</strong> ${this.approval.ticketNumber}</p>
              <p><strong>Requester:</strong> ${this.approval.requesterEmail}</p>
              <p><strong>Service:</strong> ${this.approval.serviceDescription || 'VDI Service'}</p>
              <p><strong>Justification:</strong> ${this.approval.justification}</p>
            </div>
          ` : ''}
          <textarea
            nz-input
            placeholder="Optional comments for ${isApprove ? 'approval' : 'rejection'}..."
            rows="3"
            #commentsTextarea
          ></textarea>
        </div>
      `,
      nzOkText: isApprove ? 'Approve' : 'Reject',
      nzOkType: isApprove ? 'primary' : 'default',
      nzCancelText: 'Cancel',
      nzOnOk: (componentInstance: any) => {
        const comments = componentInstance?.commentsTextarea?.value || '';
        return this.performApprovalAction(action, comments);
      },
      nzWidth: 600
    });
  }

  private performApprovalAction(action: 'approve' | 'reject', comments?: string): Promise<void> {
    const source$ = action === 'approve'
      ? this.vdiApprovalService.approveTicket(this.ticketNumber!, { comments })
      : this.vdiApprovalService.rejectTicket(this.ticketId!, comments);

    return new Promise((resolve, reject) => {
      source$.pipe(takeUntil(this.destroy$)).subscribe({
        next: (response) => {
          const actionText = action === 'approve' ? 'approved' : 'rejected';
          this.message.success(`VDI request ${actionText} successfully`);
          this.loadApprovalStatus(); // Refresh status immediately

          // 🚀 IMMEDIATE STATUS CHECKS: Poll more frequently right after approval
          if (action === 'approve') {
            console.log('🔄 [VDI Approval] Scheduling additional status checks after approval...');
            // Check after 3, 6, and 9 seconds to catch InProgress status quickly
            setTimeout(() => {
              console.log('📊 [VDI Approval] Additional status check #1 (3s after approval)');
              this.loadApprovalStatus();
            }, 3000);
            setTimeout(() => {
              console.log('📊 [VDI Approval] Additional status check #2 (6s after approval)');
              this.loadApprovalStatus();
            }, 6000);
            setTimeout(() => {
              console.log('📊 [VDI Approval] Additional status check #3 (9s after approval)');
              this.loadApprovalStatus();
            }, 9000);
          }

          resolve();
        },
        error: (error) => {
          const actionText = action === 'approve' ? 'approval' : 'rejection';
          this.message.error(`Failed to ${actionText} VDI request: ${error.error?.message || 'Unknown error'}`);
          reject(error);
        }
      });
    });
  }

  refresh(): void {
    this.loadApprovalStatus();
    this.checkApprovalPermissions();
  }

  refreshApprovalData(): void {
    if (!this.ticketId) return;

    this.loading = true;
    this.vdiApprovalService.refreshApprovalData(this.ticketId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.message.success('Approval data refreshed successfully');
          this.loadApprovalStatus(); // Reload status after refresh
        },
        error: (error) => {
          // Handle 404 - approval record doesn't exist yet
          if (error.status === 404) {
            this.message.warning('No approval record found. Creating approval record first...');
            // Try to load approval status which will create the record
            this.loadApprovalStatus();
          } else {
            this.message.error(`Failed to refresh approval data: ${error.error?.message || 'Unknown error'}`);
            this.loading = false;
          }
        }
      });
  }

  get showRefreshButton(): boolean {
    // Show refresh button if requester email is unknown/default
    return this.approval?.requesterEmail === 'unknown@system.local' ||
           this.approval?.requesterEmail?.includes('unknown') || false;
  }

  private startAutoRefresh(): void {
    console.log('🔄 [VDI Approval] Starting auto-refresh (30s interval)');
    this.refreshInterval = setInterval(() => {
      console.log('⏰ [VDI Approval] Auto-refresh triggered');

      // Stop auto-refresh if approval is in final state
      if (this.approval &&
          (this.approval.approvalStatus === 'Approved' || this.approval.approvalStatus === 'Rejected') &&
          (this.approval.vmCreationStatus === 'Succeeded' || this.approval.vmCreationStatus === 'Failed')) {
        console.log('🛑 [VDI Approval] Final state reached, stopping auto-refresh');
        this.stopAutoRefresh();
        return;
      }

      this.loadApprovalStatus();
    }, 30000); // Refresh every 30 seconds
  }

  private stopAutoRefresh(): void {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = undefined;
    }
  }

  private updateStatusInfo(): void {
    this.approvalStatusInfo = this.vdiApprovalService.getApprovalStatusInfo(this.approval);
    this.vmCreationStatusInfo = this.vdiApprovalService.getVmCreationStatusInfo(this.approval);
  }

  private updateCachedData(): void {
    // Update all cached data to avoid infinite change detection loops
    this.updateStatusInfo();
    this.updateVmDetails();
  }

  get showApprovalActions(): boolean {
    if (!this.approval || !this.canApprove) return false;
    return this.approvalStatusInfo.canApprove || this.approvalStatusInfo.canReject;
  }

  private updateVmDetails(): void {
    if (!this.approval || !this.approval.vmName) {
      this.vmDetails = [];
      return;
    }

    this.vmDetails = [
      { label: 'VM Name', value: this.approval.vmName },
      { label: 'VM Size', value: this.approval.vmSize },
      { label: 'Requested VM Size', value: this.approval.requestedVmSize },
      { label: 'Region', value: this.approval.requestedRegion },
      { label: 'Subscription', value: this.approval.subscriptionId },
      { label: 'Resource Group', value: this.approval.resourceGroupName },
      { label: 'Queue Message', value: this.approval.queueMessageId },
      { label: 'Attempt ID', value: this.approval.provisioningAttemptId },
      { label: 'Host Name', value: this.approval.hostName },
      { label: 'Public IP', value: this.approval.publicIpAddress },
      { label: 'Private IP', value: this.approval.privateIpAddress },
      { label: 'Admin Username', value: this.approval.adminUsername },
      { label: 'Admin Password', value: this.approval.adminPassword ? '••••••••' : 'Not Set' }
    ].filter(detail => detail.value);
  }

  /**
   * Load Azure VM status from Azure
   */
  loadAzureVmStatus(): void {
    if (!this.ticketNumber || !this.approval?.vmName) {
      console.log('ℹ️ [VDI Approval] Skipping Azure status check - No VM created yet');
      return;
    }

    console.log(`🔍 [VDI Approval] Loading Azure VM status for ticket: ${this.ticketNumber}`);

    this.vdiApprovalService.getAzureVmStatus(this.ticketNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          console.log('✅ [VDI Approval] Azure VM status loaded:', response);
          this.azureVmStatus = response.vmStatus;
          
          if (this.azureVmStatus) {
            // Update cached power state info
            this.azurePowerStateInfo = this.vdiApprovalService.getAzurePowerStateInfo(
              this.azureVmStatus.powerState
            );
            console.log(`⚡ [VDI Approval] Azure Power State: ${this.azurePowerStateInfo.status}`);
          }
        },
        error: (error) => {
          console.error('❌ [VDI Approval] Error loading Azure VM status:', error);
          // Don't show error message to user - Azure status is optional
        }
      });
  }

  /**
   * Start Azure VM status monitoring (refresh every 30 seconds)
   */
  startAzureStatusMonitoring(): void {
    // Wait a bit for approval data to load first
    setTimeout(() => {
      if (this.approval?.vmName && this.ticketNumber) {
        console.log('🔄 [VDI Approval] Starting Azure VM status monitoring');
        
        // Load immediately
        this.loadAzureVmStatus();
        
        // Then refresh every 30 seconds
        this.azureStatusRefreshInterval = setInterval(() => {
          this.loadAzureVmStatus();
        }, 30000); // 30 seconds
      }
    }, 2000); // Wait 2 seconds for approval data to load
  }

  /**
   * Stop Azure VM status monitoring
   */
  stopAzureStatusMonitoring(): void {
    if (this.azureStatusRefreshInterval) {
      console.log('🛑 [VDI Approval] Stopping Azure VM status monitoring');
      clearInterval(this.azureStatusRefreshInterval);
      this.azureStatusRefreshInterval = undefined;
    }
  }

  getIconTheme(color: string): 'fill' | 'outline' | 'twotone' {
    switch (color) {
      case 'success':
        return 'fill';
      case 'warning':
        return 'fill';
      case 'error':
        return 'fill';
      case 'processing':
        return 'outline'; // Changed from 'fill' - sync icon doesn't have fill variant
      default:
        return 'outline';
    }
  }

  /**
   * Handle RDP connection state changes
   */
  onRdpConnectionChanged(connection: RdpConnection): void {
    console.log('🖥️ [VDI Approval] RDP connection state changed:', connection);

    switch (connection.status) {
      case 'connected':
        this.message.success(`RDP connected to VM "${connection.vmName}"`);
        break;

      case 'disconnected':
        this.message.info(`RDP disconnected from VM "${connection.vmName}"`);
        break;

      case 'error':
        this.message.error(`RDP connection failed: ${connection.error}`);
        break;

      case 'connecting':
        console.log(`🔄 [VDI Approval] RDP connecting to VM "${connection.vmName}"`);
        break;
    }
  }

  /**
   * Copy text to clipboard
   */
  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.message.success('Copied to clipboard');
    }).catch(() => {
      this.message.error('Failed to copy to clipboard');
    });
  }
}